import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TravelMap from './TravelMap';
import TRAVEL_PROJECTS from '../data/travelProjects';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

// First-visit guide for Traveler Mode, game-tutorial style:
//   1. a popup previews the route replay (the Hokkaido loop drawing itself);
//   2. the page dims except the Hokkaido card's real "replay" button, which
//      the visitor taps themselves — so they learn where the feature lives.
// Grey areas don't dismiss it, but "skip" is always available.

const DEMO_TRIP_ID = 'hokkaido-cub';
const HOLE_PAD = 8;

function TravelTutorial({ language, onFinish }) {
  const [step, setStep] = useState('intro');
  const [hole, setHole] = useState(null);
  const primaryRef = useRef(null);
  const skipRef = useRef(null);
  const targetRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const demoTrip = TRAVEL_PROJECTS.find((trip) => trip.id === DEMO_TRIP_ID);

  const finish = useCallback((result) => onFinish(result), [onFinish]);

  // Step 1: lock the page behind the popup and focus its main action.
  useEffect(() => {
    if (step !== 'intro') {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    primaryRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [step]);

  // Step 2: bring the real button into view and keep the spotlight on it
  // while the page scrolls or resizes.
  useEffect(() => {
    if (step !== 'spotlight') {
      return undefined;
    }
    const card = document.getElementById(`travel-${DEMO_TRIP_ID}`);
    const button = card?.querySelector('.button--map');
    if (!button) {
      finish('skip');
      return undefined;
    }
    targetRef.current = button;
    button.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    button.focus({ preventScroll: true });

    let frame = null;
    let last = '';
    const track = () => {
      const rect = button.getBoundingClientRect();
      const key = `${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)},${Math.round(rect.height)}`;
      if (key !== last) {
        last = key;
        setHole({
          left: rect.left - HOLE_PAD,
          top: rect.top - HOLE_PAD,
          right: rect.right + HOLE_PAD,
          bottom: rect.bottom + HOLE_PAD,
        });
      }
      frame = window.requestAnimationFrame(track);
    };
    frame = window.requestAnimationFrame(track);

    // The visitor's own tap on the button completes the tutorial; the card
    // then opens the replay as usual.
    const handleClick = (event) => {
      if (event.target instanceof Element && event.target.closest('.button--map') === button) {
        finish('done');
      }
    };
    document.addEventListener('click', handleClick, true);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('click', handleClick, true);
    };
  }, [finish, prefersReducedMotion, step]);

  // Escape skips; in the popup, Tab cycles between its two buttons.
  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        finish('skip');
        return;
      }
      if (event.key === 'Tab' && step === 'intro') {
        const order = [primaryRef.current, skipRef.current].filter(Boolean);
        const index = order.indexOf(document.activeElement);
        event.preventDefault();
        const next = event.shiftKey ? index - 1 : index + 1;
        order[(next + order.length) % order.length]?.focus();
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [finish, step]);

  if (typeof document === 'undefined' || !demoTrip) {
    return null;
  }

  const copy =
    language === 'ja'
      ? {
          kicker: '旅の楽しみ方',
          title: '旅の行程を、地図で追体験。',
          body: '各旅のカードにある「行程を追体験する」ボタンを押すと、実際に移動したルートを、乗り物と一緒に地図の上でたどれます。まずは、行程が長くて見ごたえのある北海道一周で試してみませんか？',
          primary: '北海道一周で試してみる',
          skip: 'スキップ',
          tapTitle: 'ここをタップ！',
          tapBody: '北海道一周の行程を追体験できます',
          end: 'チュートリアルを終了',
        }
      : {
          kicker: 'How to explore',
          title: 'Relive each route on the map.',
          body: 'Every journey card has a “Replay the route” button: the real route unfolds on the map, vehicle and all. Try it first with the long, winding loop around Hokkaido.',
          primary: 'Try it with Hokkaido',
          skip: 'Skip',
          tapTitle: 'Tap here!',
          tapBody: 'Replay the loop around Hokkaido',
          end: 'End tutorial',
        };

  if (step === 'intro') {
    return createPortal(
      <div className="tutorial tutorial--intro" role="presentation">
        <div className="tutorial__dialog" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
          <p className="tutorial__kicker mono">
            {copy.kicker}
            <span className="tutorial__step">1 / 2</span>
          </p>
          <h2 id="tutorial-title" className="tutorial__title">
            {copy.title}
          </h2>
          <div className="tutorial__map" aria-hidden="true">
            <TravelMap
              routeId={demoTrip.route}
              routeKind={demoTrip.routeKind}
              cities={demoTrip.cities}
              vehicle={demoTrip.vehicle}
              language={language}
              variant="teaser"
            />
          </div>
          <p className="tutorial__body">{copy.body}</p>
          <div className="tutorial__actions">
            <button
              type="button"
              ref={primaryRef}
              className="button button--primary tutorial__primary"
              onClick={() => setStep('spotlight')}
            >
              {copy.primary} <span aria-hidden="true">▶</span>
            </button>
            <button type="button" ref={skipRef} className="tutorial__skip" onClick={() => finish('skip')}>
              {copy.skip}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Spotlight: a dim layer with a hole cut out over the button. The hole is
  // carved with clip-path, which also lets taps fall through to the button.
  const clipPath = hole
    ? `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${hole.left}px ${hole.top}px, ${hole.left}px ${hole.bottom}px, ${hole.right}px ${hole.bottom}px, ${hole.right}px ${hole.top}px, ${hole.left}px ${hole.top}px, 0 0)`
    : undefined;
  const bubbleAbove = hole ? hole.top > 150 : true;

  return createPortal(
    <div className="tutorial tutorial--spotlight" role="presentation">
      <div className="tutorial__dim" style={clipPath ? { clipPath, WebkitClipPath: clipPath } : undefined} />
      {hole ? (
        <>
          <div
            className="tutorial__ring"
            style={{
              left: hole.left,
              top: hole.top,
              width: hole.right - hole.left,
              height: hole.bottom - hole.top,
            }}
            aria-hidden="true"
          />
          <div
            className={`tutorial__bubble ${bubbleAbove ? 'is-above' : 'is-below'}`}
            style={{
              left: Math.max(16, Math.min(hole.left, window.innerWidth - 256)),
              top: bubbleAbove ? hole.top - 14 : hole.bottom + 14,
            }}
            role="status"
          >
            <span className="tutorial__step mono">2 / 2</span>
            <strong>{copy.tapTitle}</strong>
            <span>{copy.tapBody}</span>
          </div>
        </>
      ) : null}
      <button type="button" className="tutorial__end" onClick={() => finish('skip')}>
        {copy.end}
      </button>
    </div>,
    document.body
  );
}

export default TravelTutorial;
